import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import {createUseStyles} from "react-jss";
import {HostBubbleContext} from "../../contexts/HostBubbleContext";
import {useResizeObserver} from "engage-client/hooks/useResizeObserver";
import {Theme} from "engage-client/theme";
import {EngageAppContext} from "../../App";
import {TriviaStatus} from "../../state/state";

const useBubbleStyles = createUseStyles<Theme>((theme) => ({
    hostBubble: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "absolute",
        width: ({width}) => width,
        height: ({height}) => height,
        zIndex: ({complete}) => complete ? 10 : 0,
    },
    videoBubble: {
        width: "100%",
        height: "100%",
        borderRadius: ({rectangle, width}) => rectangle ? 0 : width/2,
        "-webkit-mask-image": "-webkit-radial-gradient(white, black)",
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "absolute",
        [theme.breakpoints.up("lg")]: {
            border: ({starting, rectangle}) => rectangle ? "4px solid #003665" : starting ?  "10px solid #003665" : "0px"
        },
        [theme.breakpoints.down("md")]: {
            border: ({starting, complete, rectangle}) => rectangle ? "4px solid #003665" :starting || !complete ?  "10px solid #003665" : "0px"
        },
    },
    bubble: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: ({width}) => width * 2,
        height: ({height}) => height * 2,
        position: "absolute"
    },
    muteOverlay: {
        zIndex: ({complete}) => complete ? 15 : 1,
        position: "absolute",
        bottom: ({rectangle, height, muteRatio}) => rectangle ? - (height/muteRatio) / 3 : 0,
        right: ({rectangle, height, muteRatio}) => rectangle ? - (height/muteRatio) / 3 : 0,
        width: ({height, muteRatio}) => height/muteRatio, // this should be a square either way, so use height.
        height: ({height, muteRatio}) => height/muteRatio,
        borderRadius: ({height, muteRatio}) => height / (muteRatio*1.5),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: theme.presenterVideo.background,
        cursor: "pointer",
        [theme.breakpoints.up("lg")]:{
            //TODO: why does it resize here?
            background: ({starting, complete}) => !(starting || complete) ? theme.presenterVideo.muteBackground : "#003665"
        }
    },
    muteIcon: {
        width: ({height, muteRatio}) => height / (muteRatio*1.5), // this should be a square either way, so use height.
        height: ({height, muteRatio}) => height / (muteRatio*1.5),
        [theme.breakpoints.up("lg")]: {
            filter: ({starting, complete}) => !(starting || complete) ? "invert(1)" : null
        }
    }
}));

export type HostBubbleProps = {
    embedURL: string
    width?: number
    dep?: any
    mute?: boolean
}

export function validateAndPullID(embedURL: string):string|undefined {
    //Pulled from here: https://stackoverflow.com/a/45426669/413443
    const youtubeRegex = /^.*(youtu\.be\/|vi?\/|u\/\w\/|embed\/|\?vi?=|&vi?=)([^#&?]*).*/;
    const parsed = embedURL.match(youtubeRegex);
    if (parsed?.[2]) {
        return parsed[2];
    } else {
        return undefined;
    }
}

export const HostBubble = (props: Partial<HostBubbleProps>) => {
    const ctx = useContext(HostBubbleContext);
    const {appState} = useContext(EngageAppContext.current);

    const starting = appState.common.state === TriviaStatus.Starting;
    const complete = appState.common.state === TriviaStatus.Complete;
    const rectangle = appState.common.gameOptions.introVideoRectangle && starting;

    const width = ctx?.width ?? props.width ?? 200;
    const height = rectangle ? width * (9/16) : width;
    const embedURL = ctx?.embedURL ?? props.embedURL;

    const [mute, setMute] = useState(props?.mute ?? false);
    const [player, setPlayer] = useState<YT.Player>();
    const playerRef = useRef(null);
    const videoId = validateAndPullID(embedURL ?? "");

    const muteRatio = rectangle ? 5 : complete ? 3 : 4;

    
    const classes = useBubbleStyles({rectangle, width, height, starting, complete, muteRatio});

    const createYTPlayer = useCallback(() => {
        if(player) {
            player.destroy();
        }
        if(videoId) {
            setPlayer(new YT.Player("player", {
                    height: rectangle ? height : height * 2,
                    width: rectangle ? width : width * 2,
                    videoId: videoId,
                    playerVars: {autoplay: 1, controls: 0, modestbranding: 1, fs: 0, playsinline: 1, rel:0},
                    events: {
                        onReady: onPlayerReady
                    }
                }
            ));
        }
    },[setPlayer,embedURL]);
    
    useEffect(() => {
        if(player) {
            player.setSize(rectangle ? width : width * 2, rectangle ? height : height * 2);
        }
    },[width, height, rectangle, player]);
    
    
    useEffect(() => {
        // load youtube if YT not available & there's a player div rendered
        if (!window.YT && playerRef.current) {
            const tag = document.createElement("script");
            tag.src = "https://www.youtube.com/iframe_api";
            const firstScriptTag = document.getElementsByTagName("script")[0];
            firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
            //@ts-ignore
            window.onYouTubeIframeAPIReady = function() {
                createYTPlayer();
            };
        }
        // only create new player if there's a div rendered
        else if(playerRef.current) {
            createYTPlayer();
        }
    },[createYTPlayer, playerRef]);
    
    const toggleMute = () => {
        if(player) {
            setMute(!player.isMuted());
            if (player.isMuted()) {
                player.unMute();
            } else {
                player.mute();
            }
            
        }
    };
    
    function onPlayerReady(event:any) {
        event.target.playVideo();
        if(mute) {
            event.target.mute();
        }
    }
    
    if(videoId) {
        return <div className={classes.hostBubble} style={{left: ctx?.left ?? 0, top: ctx?.top ?? 0}}>
            <div className={classes.videoBubble}>
            <div className={classes.bubble}>
                {/* this is where youtube player will be replaced */}
                <div id="player" ref={playerRef}></div>
                </div>
            </div>
            <div className={classes.muteOverlay} onClick={toggleMute}>
                <img className={classes.muteIcon} src={mute ? "/assets/icons/common/mute.svg" : "/assets/icons/common/speaker.svg"} />
            </div>
        </div>;
    }
    return null;
};

export const HostBubblePlaceHolder = ({width = 200, embedURL, dep}: HostBubbleProps) => {
    const {setTop, setLeft, setWidth, setEmbedURL, refreshPosition, setRefreshPosition} = useContext(HostBubbleContext);
    const {appState} = useContext(EngageAppContext.current);
    const rectangle = appState.common.gameOptions.introVideoRectangle && appState.common.state === TriviaStatus.Starting;
    const el = useRef<HTMLDivElement>(null);

    // Pass handle in as a dependency so we re-register a new resize listener when we update it's value.
    useResizeObserver(() => {
        if(el.current !== null) {
            const rect = el.current.getBoundingClientRect();
            setLeft(rect.left);
            setTop(rect.top);
        }
    }, [setLeft, setTop, dep]);

    useEffect( () => {
        if(refreshPosition) {
            if(el.current !== null) {
                const rect = el.current.getBoundingClientRect();
                setLeft(rect.left);
                setTop(rect.top);
            }
            setRefreshPosition(false);
        }
    }, [refreshPosition, setLeft, setRefreshPosition, setTop]);

    useEffect(() => {
        setWidth(width);
        setEmbedURL(embedURL);
        return () => setEmbedURL(null); // Remove the absolutely positioned host bubble.
    }, [width, embedURL, setWidth, setEmbedURL]);

    return <div ref={el} style={{width: width, height: rectangle ? width * (9/16) : width}}/>;
};
